import React, { useState } from 'react';
import { Menu, X, ArrowRight, Shield, Zap, Brain, ChevronRight } from 'lucide-react';

const LumenAgentsWebsite = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', page: 'home' },
    { name: 'Privacy Policy', page: 'privacy' },
    { name: 'Terms of Service', page: 'terms' }
  ];

  const Header = () => (
    <header className="fixed w-full top-0 z-50 bg-black/80 backdrop-blur-md border-b border-gray-800">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <button 
              onClick={() => setCurrentPage('home')}
              className="text-2xl font-bold bg-gradient-to-r from-gray-200 to-gray-400 bg-clip-text text-transparent hover:from-white hover:to-gray-300 transition-all"
            >
              LumenAgents.AI
            </button>
          </div>
          
          <div className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => setCurrentPage(item.page)}
                className={`text-sm transition-colors ${
                  currentPage === item.page
                    ? 'text-white'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          <button
            className="md:hidden text-gray-400 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  setCurrentPage(item.page);
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-4 py-3 text-sm transition-colors ${
                  currentPage === item.page
                    ? 'text-white bg-gray-900'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/50'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}
      </nav>
    </header>
  );

  const HomePage = () => (
    <div className="min-h-screen bg-black text-white">
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-gray-300 to-gray-500 bg-clip-text text-transparent">
              Intelligent Agents for
              <br />
              Modern Enterprises
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Building custom AI agents tailored to your business needs, from initial concept to enterprise deployment.
            </p>
            <a
              href="mailto:tech@lumenagents.ai"
              className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 transition-all group"
            >
              Get Started
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-24">
            <div className="bg-gradient-to-br from-gray-900 to-gray-950 p-8 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-6">
                <Brain className="text-gray-300" size={24} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Custom AI Agents</h3>
              <p className="text-gray-400">
                Designed and developed specifically for your business workflows and objectives.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-950 p-8 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-6">
                <Zap className="text-gray-300" size={24} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Enterprise Training</h3>
              <p className="text-gray-400">
                Model training and fine-tuning with your proprietary data for optimal performance.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-950 p-8 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all">
              <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-6">
                <Shield className="text-gray-300" size={24} />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Full Deployment</h3>
              <p className="text-gray-400">
                End-to-end deployment solutions with ongoing support and maintenance.
              </p>
            </div>
          </div>

          <div className="mt-32 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Contact us to discuss how AI agents can optimize your operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:tech@lumenagents.ai"
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all border border-gray-700"
              >
                tech@lumenagents.ai
              </a>
              <a
                href="mailto:support@lumenagents.ai"
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all border border-gray-700"
              >
                support@lumenagents.ai
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const PrivacyPolicy = () => (
    <div className="min-h-screen bg-black text-white">
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <button
              onClick={() => setCurrentPage('home')}
              className="text-gray-400 hover:text-white flex items-center mb-6 transition-colors"
            >
              <ChevronRight className="rotate-180 mr-2" size={20} />
              Back to Home
            </button>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-gray-400">Last updated: October 21, 2025</p>
          </div>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p className="leading-relaxed">
                LumenAgents.AI ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Contact information such as name and email address</li>
                <li>Business information and communication preferences</li>
                <li>Data provided for AI agent training and customization</li>
                <li>Technical data and usage information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Encryption and Security</h2>
              <p className="leading-relaxed mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All data transmitted to and from our AI agents is encrypted using TLS/SSL protocols</li>
                <li>Data at rest is encrypted using AES-256 encryption standards</li>
                <li>We employ secure data centers with physical and digital access controls</li>
                <li>Regular security audits and penetration testing are performed</li>
                <li>Employee access to data is strictly controlled and monitored</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">
                We use the collected information for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Providing and improving our AI agent services</li>
                <li>Training and customizing models based on your requirements</li>
                <li>Communicating with you about our services</li>
                <li>Analyzing usage patterns to enhance performance</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Sharing and Disclosure</h2>
              <p className="leading-relaxed">
                We do not sell your personal information. We may share your information only in the following circumstances: with your explicit consent, to comply with legal obligations, to protect our rights and property, or with service providers who assist in our operations under strict confidentiality agreements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Rights</h2>
              <p className="leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal information</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of certain data processing activities</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
              <p className="leading-relaxed">
                We retain your information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p className="leading-relaxed mb-4">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2">
                <p>Email: <a href="mailto:support@lumenagents.ai" className="text-gray-400 hover:text-white transition-colors">support@lumenagents.ai</a></p>
                <p>Technical inquiries: <a href="mailto:tech@lumenagents.ai" className="text-gray-400 hover:text-white transition-colors">tech@lumenagents.ai</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  const TermsOfService = () => (
    <div className="min-h-screen bg-black text-white">
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <button
              onClick={() => setCurrentPage('home')}
              className="text-gray-400 hover:text-white flex items-center mb-6 transition-colors"
            >
              <ChevronRight className="rotate-180 mr-2" size={20} />
              Back to Home
            </button>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-gray-400">Last updated: October 21, 2025</p>
          </div>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Agreement to Terms</h2>
              <p className="leading-relaxed">
                By accessing or using LumenAgents.AI services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Services Description</h2>
              <p className="leading-relaxed">
                LumenAgents.AI provides custom AI agent development, enterprise model training, and deployment services. We reserve the right to modify, suspend, or discontinue any aspect of our services at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">User Responsibilities</h2>
              <p className="leading-relaxed mb-4">
                When using our services, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use our services in compliance with all applicable laws</li>
                <li>Not attempt to interfere with or disrupt our services</li>
                <li>Not use our services for any unlawful or prohibited purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Intellectual Property</h2>
              <p className="leading-relaxed">
                All content, features, and functionality of our services are owned by LumenAgents.AI and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of any data you provide to us for AI agent training.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Security and Privacy</h2>
              <p className="leading-relaxed mb-4">
                We implement robust security measures including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>End-to-end encryption for all data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Strict access controls and authentication protocols</li>
                <li>Compliance with industry-standard security practices</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Service Level and Availability</h2>
              <p className="leading-relaxed">
                While we strive to maintain high availability, we do not guarantee uninterrupted access to our services. Scheduled maintenance and unforeseen technical issues may affect service availability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Limitation of Liability</h2>
              <p className="leading-relaxed">
                To the maximum extent permitted by law, LumenAgents.AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Confidentiality</h2>
              <p className="leading-relaxed">
                We treat all client data and business information as strictly confidential. We will not disclose your proprietary information to third parties without your explicit consent, except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Termination</h2>
              <p className="leading-relaxed">
                We reserve the right to terminate or suspend access to our services immediately, without prior notice, for any breach of these Terms of Service or for any other reason at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of any material changes. Your continued use of our services after such modifications constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Governing Law</h2>
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Information</h2>
              <p className="leading-relaxed mb-4">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2">
                <p>General inquiries: <a href="mailto:support@lumenagents.ai" className="text-gray-400 hover:text-white transition-colors">support@lumenagents.ai</a></p>
                <p>Technical support: <a href="mailto:tech@lumenagents.ai" className="text-gray-400 hover:text-white transition-colors">tech@lumenagents.ai</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = () => (
    <footer className="bg-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-bold mb-4">LumenAgents.AI</h3>
            <p className="text-gray-400 text-sm">
              Custom AI agents for modern enterprises.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <div className="space-y-2">
              <button
                onClick={() => setCurrentPage('privacy')}
                className="block text-gray-400 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setCurrentPage('terms')}
                className="block text-gray-400 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-sm">
              <a
                href="mailto:tech@lumenagents.ai"
                className="block text-gray-400 hover:text-white transition-colors"
              >
                tech@lumenagents.ai
              </a>
              <a
                href="mailto:support@lumenagents.ai"
                className="block text-gray-400 hover:text-white transition-colors"
              >
                support@lumenagents.ai
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} LumenAgents.AI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-black">
      <Header />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'privacy' && <PrivacyPolicy />}
      {currentPage === 'terms' && <TermsOfService />}
      <Footer />
    </div>
  );
};

export default LumenAgentsWebsite;